/**
 * BdcDashboardWidgets — two dashboard widgets mounted on `/bdc`:
 *
 *   - MyClaimedLeadsWidget → GET /api/bdc/my-claimed-leads. Top 8 leads
 *     currently claimed by the logged-in agent, sorted by last_activity_at
 *     descending. Clicking a row jumps to the workspace.
 *   - MyEarningsWidget → GET /api/bdc/dashboard/earnings. Current-month
 *     total $50 × showed-up count, plus YTD, plus a payout-status pill.
 *
 * BOTH widgets scope themselves to the current agent server-side — no
 * client-side filtering. BDC agents never see the $300 dealer charge.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Banknote, CheckCircle2, ChevronRight, Clock, MessageCircle, Sparkles, X,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatUSD } from "@/lib/format";
import { StatusPill, LEAD_STATUS } from "./bdcUi";

const cents = (n) => formatUSD((n || 0) / 100);

const fmtRelative = (iso) => {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
};

export const MyClaimedLeadsWidget = () => {
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/bdc/my-claimed-leads", { params: { limit: 8 } });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="my-claimed-leads-widget">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Your work</p>
          <h3 className="font-display text-lg font-semibold text-slate-900">My claimed leads</h3>
        </div>
        <span className="rounded-full bg-brand text-white text-xs font-bold px-2.5 py-1" data-testid="my-claimed-leads-count">
          {data.total}
        </span>
      </div>

      <ul className="mt-4 divide-y divide-slate-100">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <li key={i} className="py-3">
              <div className="h-10 rounded-md bg-slate-100 animate-pulse" />
            </li>
          ))
        ) : data.items.length === 0 ? (
          <li className="py-6 text-center text-sm text-slate-400 italic">
            No claimed leads yet — grab one from the queue.
          </li>
        ) : (
          data.items.map((l) => (
            <li key={l.id}>
              <Link
                to={`/bdc/leads/${l.id}`}
                className="flex items-center gap-3 py-2.5 hover:bg-slate-50 -mx-2 px-2 rounded-md transition-colors"
                data-testid={`my-claimed-lead-${l.id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm text-slate-900 truncate">
                    {l.customer_name || l.customer_email || "Guest"}
                  </p>
                  <p className="text-[11px] text-slate-500 truncate">
                    {l.vehicle ? `${l.vehicle.year} ${l.vehicle.make} ${l.vehicle.model}` : "No vehicle attached"}
                  </p>
                </div>
                <StatusPill tone={LEAD_STATUS[l.status]?.tone || "slate"}>
                  {LEAD_STATUS[l.status]?.label || l.status}
                </StatusPill>
                <span className="text-[10px] text-slate-400 hidden sm:inline">{fmtRelative(l.last_activity_at)}</span>
                {l.unread_chat_count > 0 && (
                  <span className="inline-flex items-center gap-1 text-[10px] rounded-full bg-brand text-white font-bold px-1.5 py-0.5">
                    <MessageCircle size={9} /> {l.unread_chat_count}
                  </span>
                )}
                <ChevronRight size={14} className="text-slate-300 shrink-0" />
              </Link>
            </li>
          ))
        )}
      </ul>

      <Link
        to="/bdc/queue?assignment=mine"
        className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-brand hover:text-brand-hover"
        data-testid="my-claimed-leads-see-all"
      >
        See all <ChevronRight size={12} />
      </Link>
    </div>
  );
};

export const MyEarningsWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/bdc/dashboard/earnings")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const payoutMeta = {
    verified:               { label: "ACH verified",         cls: "bg-emerald-100 text-emerald-800", icon: <CheckCircle2 size={11} /> },
    pending_verification:   { label: "Awaiting verification", cls: "bg-amber-100 text-amber-800",    icon: <Clock size={11} /> },
    not_uploaded:           { label: "ACH form missing",     cls: "bg-rose-100 text-rose-800",       icon: <X size={11} /> },
  }[data?.payout_status || "not_uploaded"];

  return (
    <div
      className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-white p-5"
      data-testid="my-earnings-widget"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-widest font-semibold text-amber-700">Your earnings</p>
          <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-1.5">
            <Sparkles size={14} className="text-amber-500" /> This month
          </h3>
        </div>
        {payoutMeta && (
          <span className={`inline-flex items-center gap-1 rounded-full text-[10px] font-bold px-2 py-1 ${payoutMeta.cls}`} data-testid="my-earnings-payout-status">
            {payoutMeta.icon} {payoutMeta.label}
          </span>
        )}
      </div>

      {loading ? (
        <div className="mt-4 h-20 rounded-md bg-slate-100 animate-pulse" />
      ) : (
        <>
          <p className="mt-3 font-display text-4xl font-semibold text-slate-900" data-testid="my-earnings-month-total">
            {cents(data?.total_commission_cents)}
          </p>
          <p className="text-[11px] text-slate-500">
            {data?.showed_up_count || 0} showed up · {cents(data?.commission_per_appt_cents)} each · {data?.pending_appointments_this_month || 0} pending this month
          </p>

          <div className="mt-4 grid grid-cols-2 gap-3 pt-3 border-t border-amber-100">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">YTD showed up</p>
              <p className="font-display text-lg font-semibold text-slate-900 mt-0.5">{data?.ytd_showed_up_count || 0}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">YTD earnings</p>
              <p className="font-display text-lg font-semibold text-slate-900 mt-0.5">{cents(data?.ytd_commission_cents)}</p>
            </div>
          </div>
        </>
      )}

      <Link
        to="/bdc/settings/payment"
        className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-800 hover:text-amber-900"
        data-testid="my-earnings-payout-link"
      >
        <Banknote size={12} /> Manage payout details <ChevronRight size={11} />
      </Link>
    </div>
  );
};
